import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/auth-context";
import { useTheme } from "../context/ThemeContext";
import logviComplete from "../assets/icons/logvi-complete.png";

/** Resolver de avatar para CRA/Webpack */
const profileCtx: any = (require as any).context(
  "../assets/profile",
  false,
  /\.(jpe?g|png)$/i
);

const API_BASE = "https://api.logvi-havox.com";
const STORAGE_KEY_SELECTED_COMPANY = "eva:home:selected-company";
const STORAGE_KEY_SELECTED_ADDRESS = "eva:leftbar:selected-address-global";

type EmpresaAcessivel = {
  id: number | string;
  nome: string;
  label?: string | null;
  razao_social?: string | null;
  nome_fantasia?: string | null;
  cnpj?: string | null;
  papel?: string | null;
  ambiente_padrao?: boolean | null;
  tipo_ambiente?: string | null;
};

type CompanyOption = {
  value: string;
  label: string;
  subtitle: string;
  nome: string;
  razaoSocial: string;
  cnpj?: string | null;
  tipoAmbiente?: string | null;
  papel?: string | null;
};

function hashIndex(seed: string): number {
  let h = 0;

  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }

  return h % 6;
}

function resolveProfileUrl(
  pictureId?: string | number,
  fallbackSeed?: string
): string | undefined {
  if (pictureId !== undefined && pictureId !== null) {
    const id = String(pictureId).trim();
    const candidates = [`./${id}.jpg`, `./${id}.jpeg`, `./${id}.png`];

    for (const c of candidates) {
      try {
        return profileCtx(c) as string;
      } catch {}
    }
  }

  const seed = String(pictureId ?? fallbackSeed ?? "default");
  const idx = hashIndex(seed) + 1;

  const fallbackCandidates = [
    `./profile-${idx}.jpg`,
    `./profile-${idx}.jpeg`,
    `./profile-${idx}.png`,
  ];

  for (const c of fallbackCandidates) {
    try {
      return profileCtx(c) as string;
    } catch {}
  }

  return undefined;
}

function getInitials(fullName: string) {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const second = parts.length > 1 ? parts[1]?.[0] ?? "" : "";

  return (first + second).toUpperCase();
}

function getDisplayName(fullName?: string | null) {
  const clean = String(fullName || "").trim();

  if (!clean) return "Usuário";

  const parts = clean.split(/\s+/).filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0]} ${parts[1]}`;
  }

  return parts[0];
}

function formatCnpj(cnpj?: string | null) {
  const digits = String(cnpj || "").replace(/\D/g, "");

  if (digits.length !== 14) return cnpj || "";

  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(
    5,
    8
  )}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
}

function toNullableString(value: any): string | null {
  if (value === undefined || value === null) return null;

  const clean = String(value).trim();

  return clean.length > 0 ? clean : null;
}

function cleanCompanyName(value: any): string | null {
  const raw = toNullableString(value);

  if (!raw) return null;

  return raw
    .replace(/\s*-\s*produção$/i, "")
    .replace(/\s*-\s*producao$/i, "")
    .replace(/\s*-\s*homologação$/i, "")
    .replace(/\s*-\s*homologacao$/i, "")
    .replace(/\s*-\s*desenvolvimento$/i, "")
    .replace(/\s*-\s*development$/i, "")
    .trim();
}

function readArrayFromPossiblePaths(source: any, paths: string[][]): any[] {
  for (const path of paths) {
    let current = source;

    for (const key of path) {
      current = current?.[key];
    }

    if (Array.isArray(current)) {
      return current;
    }
  }

  return [];
}

function normalizeEmpresa(raw: any): EmpresaAcessivel | null {
  if (!raw || raw.id === undefined || raw.id === null) {
    return null;
  }

  const razaoSocial = cleanCompanyName(raw.razao_social);
  const nomeFantasia = cleanCompanyName(raw.nome_fantasia);
  const nome = cleanCompanyName(raw.nome);
  const label = cleanCompanyName(raw.label);

  const displayName =
    razaoSocial || nomeFantasia || nome || label || `Empresa ${raw.id}`;

  return {
    id: raw.id,
    nome: displayName,
    label: displayName,
    razao_social: razaoSocial,
    nome_fantasia: nomeFantasia,
    cnpj: toNullableString(raw.cnpj),
    papel: toNullableString(raw.papel),
    ambiente_padrao: raw.ambiente_padrao === true,
    tipo_ambiente: toNullableString(raw.tipo_ambiente ?? raw.tipoAmbiente),
  };
}

function buildCompanyOptions(empresas: EmpresaAcessivel[]): CompanyOption[] {
  return empresas.map((empresa) => {
    const razaoSocial =
      cleanCompanyName(empresa.razao_social) ||
      cleanCompanyName(empresa.nome) ||
      cleanCompanyName(empresa.label) ||
      cleanCompanyName(empresa.nome_fantasia) ||
      `Empresa ${empresa.id}`;

    const nome =
      cleanCompanyName(empresa.nome) ||
      cleanCompanyName(empresa.nome_fantasia) ||
      razaoSocial;

    const cnpjFormatado = formatCnpj(empresa.cnpj);

    return {
      value: String(empresa.id),
      label: razaoSocial,
      subtitle: cnpjFormatado || "Empresa vinculada",
      nome,
      razaoSocial,
      cnpj: empresa.cnpj || null,
      tipoAmbiente: empresa.tipo_ambiente || null,
      papel: empresa.papel || null,
    };
  });
}

function getStoredAccessToken(): string {
  try {
    const keys = ["accessToken", "eva:accessToken", "access_token"];

    for (const key of keys) {
      const value = localStorage.getItem(key);
      if (value && value.trim()) return value.trim();
    }
  } catch {}

  return "";
}

const HeaderHome: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const auth = useAuth() as any;
  const sessao = auth?.sessao ?? null;
  const logout = auth?.logout;
  const refresh = auth?.refresh;
  const switchingEmpresa = auth?.switchingEmpresa === true;
  const apiBase = String(auth?.apiBase || API_BASE).replace(/\/+$/, "");

  const { systemTitle } = useTheme();

  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const companyMenuRef = useRef<HTMLDivElement | null>(null);

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [companyMenuOpen, setCompanyMenuOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [switchingCompanyLocal, setSwitchingCompanyLocal] = useState(false);
  const [userAreaHovered, setUserAreaHovered] = useState(false);
  const [meFallback, setMeFallback] = useState<any>(null);

  const nome = sessao?.nome || "";
  const departamento = sessao?.departamento || "Departamento não identificado";

  const empresaRazaoSocial =
    cleanCompanyName(sessao?.empresa_razao_social) ||
    cleanCompanyName(sessao?.empresa?.razao_social) ||
    cleanCompanyName(sessao?.empresa_nome) ||
    cleanCompanyName(meFallback?.sessao?.empresa_razao_social) ||
    cleanCompanyName(meFallback?.empresa?.razao_social) ||
    "Empresa não identificada";

  const empresaCnpj =
    sessao?.empresa_cnpj ||
    sessao?.empresa?.cnpj ||
    meFallback?.sessao?.empresa_cnpj ||
    meFallback?.empresa?.cnpj ||
    "";

  const pictureId = sessao?.picture != null ? String(sessao.picture) : "";

  const displayName = useMemo(() => getDisplayName(nome), [nome]);

  const profileUrl = useMemo(
    () => resolveProfileUrl(pictureId, nome),
    [pictureId, nome]
  );

  const hasName = !!nome;

  const loadMeFallback = async () => {
    try {
      const res = await fetch(`${apiBase}/auth/me-hml`, {
        method: "GET",
        credentials: "include",
        cache: "no-store",
      });

      if (!res.ok) return null;

      const data = await res.json().catch(() => null);
      setMeFallback(data);

      return data;
    } catch (err) {
      console.error("HEADER_HOME_ME_FALLBACK_ERROR", err);
      return null;
    }
  };

  useEffect(() => {
    let alive = true;

    async function run() {
      const data = await loadMeFallback();

      if (!alive || !data) return;

      setMeFallback(data);
    }

    run();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const empresasAcessiveis = useMemo<EmpresaAcessivel[]>(() => {
    const rawFromSessao = readArrayFromPossiblePaths(sessao, [
      ["empresas_acessiveis"],
      ["sessao", "empresas_acessiveis"],
      ["usuario", "empresas_acessiveis"],
    ]);

    const rawFromFallback = readArrayFromPossiblePaths(meFallback, [
      ["empresas_acessiveis"],
      ["sessao", "empresas_acessiveis"],
      ["usuario", "empresas_acessiveis"],
    ]);

    const raw = rawFromSessao.length > 0 ? rawFromSessao : rawFromFallback;

    const mapped = raw
      .map((empresa: any) => normalizeEmpresa(empresa))
      .filter(
        (empresa: EmpresaAcessivel | null): empresa is EmpresaAcessivel =>
          empresa !== null
      );

    if (mapped.length > 0) {
      return mapped;
    }

    const fallbackEmpresaId =
      sessao?.empresa_ativa_id ||
      sessao?.empresa_id ||
      sessao?.empresa?.id ||
      meFallback?.sessao?.empresa_ativa_id ||
      meFallback?.empresa_ativa_id ||
      meFallback?.empresa_id ||
      meFallback?.empresa?.id;

    if (fallbackEmpresaId !== undefined && fallbackEmpresaId !== null) {
      const fallbackRazaoSocial =
        cleanCompanyName(sessao?.empresa?.razao_social) ||
        cleanCompanyName(sessao?.empresa_razao_social) ||
        cleanCompanyName(meFallback?.empresa?.razao_social) ||
        cleanCompanyName(meFallback?.sessao?.empresa_razao_social) ||
        cleanCompanyName(sessao?.empresa_nome) ||
        cleanCompanyName(meFallback?.sessao?.empresa_nome) ||
        `Empresa ${fallbackEmpresaId}`;

      return [
        {
          id: fallbackEmpresaId,
          nome: fallbackRazaoSocial,
          label: fallbackRazaoSocial,
          razao_social: fallbackRazaoSocial,
          nome_fantasia:
            cleanCompanyName(sessao?.empresa?.nome_fantasia) ||
            cleanCompanyName(sessao?.empresa_nome_fantasia) ||
            cleanCompanyName(meFallback?.empresa?.nome_fantasia),
          cnpj:
            sessao?.empresa_cnpj ||
            sessao?.empresa?.cnpj ||
            meFallback?.empresa?.cnpj ||
            meFallback?.sessao?.empresa_cnpj ||
            null,
          papel: null,
          ambiente_padrao: true,
          tipo_ambiente:
            sessao?.empresa_tipo_ambiente ||
            meFallback?.sessao?.empresa_tipo_ambiente ||
            null,
        },
      ];
    }

    return [];
  }, [sessao, meFallback]);

  const companyOptions = useMemo(
    () => buildCompanyOptions(empresasAcessiveis),
    [empresasAcessiveis]
  );

  const activeCompanyId = useMemo(() => {
    const id =
      sessao?.empresa_ativa_id ||
      sessao?.empresa_id ||
      sessao?.empresa?.id ||
      meFallback?.sessao?.empresa_ativa_id ||
      meFallback?.empresa_ativa_id ||
      meFallback?.empresa_id ||
      meFallback?.empresa?.id ||
      null;

    return id !== null && id !== undefined ? String(id) : "";
  }, [sessao, meFallback]);

  const selectedCompany = useMemo(() => {
    if (activeCompanyId) {
      const found = companyOptions.find(
        (item) => item.value === activeCompanyId
      );

      if (found) return found;
    }

    return companyOptions[0] || null;
  }, [companyOptions, activeCompanyId]);

  const selectedCompanyCnpj = useMemo(() => {
    return formatCnpj(selectedCompany?.cnpj || empresaCnpj);
  }, [selectedCompany, empresaCnpj]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const target = e.target as Node;

      if (
        userMenuOpen &&
        userMenuRef.current &&
        !userMenuRef.current.contains(target)
      ) {
        setUserMenuOpen(false);
      }

      if (
        companyMenuOpen &&
        companyMenuRef.current &&
        !companyMenuRef.current.contains(target)
      ) {
        setCompanyMenuOpen(false);
      }
    }

    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setUserMenuOpen(false);
        setCompanyMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);

    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [userMenuOpen, companyMenuOpen]);

  const handleCompanyChange = async (empresaId: string) => {
    if (!empresaId || empresaId === activeCompanyId) {
      setCompanyMenuOpen(false);
      return;
    }

    if (switchingEmpresa || switchingCompanyLocal) {
      return;
    }

    setSwitchingCompanyLocal(true);

    try {
      localStorage.setItem(
        STORAGE_KEY_SELECTED_COMPANY,
        JSON.stringify(empresaId)
      );
      localStorage.removeItem(STORAGE_KEY_SELECTED_ADDRESS);

      const accessToken = getStoredAccessToken();

      const res = await fetch(`${apiBase}/auth/switch-company-hml`, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          empresa_id: Number(empresaId),
          empresa_ativa_id: Number(empresaId),
        }),
      });

      const responseBody = await res.json().catch(() => null);

      if (!res.ok || responseBody?.ok === false) {
        throw new Error(
          responseBody?.erro ||
            responseBody?.message ||
            `Falha ao trocar empresa. HTTP ${res.status}`
        );
      }

      setCompanyMenuOpen(false);

      if (typeof refresh === "function") {
        await refresh();
      }

      await queryClient.invalidateQueries();
      await loadMeFallback();

      window.dispatchEvent(
        new CustomEvent("eva:empresa-alterada", {
          detail: {
            empresa_id: Number(empresaId),
            empresa_ativa_id: Number(empresaId),
            response: responseBody,
          },
        })
      );
    } catch (err) {
      console.error("HEADER_SWITCH_COMPANY_ERROR", err);

      try {
        localStorage.removeItem(STORAGE_KEY_SELECTED_COMPANY);
      } catch {}

      alert(
        err instanceof Error
          ? err.message
          : "Não foi possível trocar a empresa selecionada."
      );
    } finally {
      setSwitchingCompanyLocal(false);
    }
  };

  const doLogout = async () => {
    if (loggingOut) return;

    setLoggingOut(true);
    setUserMenuOpen(false);

    try {
      try {
        await fetch(`${apiBase}/auth/logout`, {
          method: "POST",
          credentials: "include",
        });
      } catch {}

      try {
        localStorage.removeItem("access_token");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("eva:accessToken");
        localStorage.removeItem(STORAGE_KEY_SELECTED_COMPANY);
        localStorage.removeItem(STORAGE_KEY_SELECTED_ADDRESS);
        sessionStorage.removeItem("github_oauth_state");
      } catch {}

      if (typeof logout === "function") {
        await logout();
      }

      navigate("/login", { replace: true });
    } finally {
      setLoggingOut(false);
    }
  };

  const isSwitchingCompany = switchingEmpresa || switchingCompanyLocal;

  return (
    <motion.header
      style={styles.container}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 260, damping: 26 }}
    >
      <motion.div
        initial={{ opacity: 0, x: -8 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
        style={styles.leftBlock}
      >
        <div style={styles.systemInfo}>
          <button
            type="button"
            style={styles.logoButton}
            onClick={() => navigate("/acess/home")}
            aria-label="Ir para Home"
          >
            <img
              src={logviComplete}
              alt="Logvi"
              style={styles.systemLogo}
              draggable={false}
            />

            <span style={styles.separator}>|</span>

            <span style={styles.systemTitle}>{systemTitle || "Sistema"}</span>
          </button>

          <span style={styles.separator}>|</span>

          <div style={styles.companySwitcher} ref={companyMenuRef}>
            <button
              type="button"
              style={{
                ...styles.companyButton,
                ...(companyMenuOpen
                  ? styles.companyButtonOpen
                  : styles.companyButtonClosed),
                ...(isSwitchingCompany ? styles.companyButtonDisabled : {}),
              }}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              onClick={(e) => {
                e.stopPropagation();

                if (isSwitchingCompany) return;

                const nextOpen = !companyMenuOpen;

                setCompanyMenuOpen(nextOpen);
                setUserMenuOpen(false);

                if (!nextOpen) {
                  e.currentTarget.blur();
                }
              }}
              onBlur={(e) => {
                if (!companyMenuOpen) {
                  e.currentTarget.blur();
                }
              }}
              disabled={isSwitchingCompany}
              aria-haspopup="menu"
              aria-expanded={companyMenuOpen}
            >
              <span style={styles.companyInfo}>
                {selectedCompany?.razaoSocial ||
                  selectedCompany?.nome ||
                  empresaRazaoSocial}
                {selectedCompanyCnpj ? ` ${selectedCompanyCnpj}` : ""}
              </span>

              <span
                style={{
                  ...styles.companyChevron,
                  transform: companyMenuOpen
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                }}
                aria-hidden="true"
              >
                {isSwitchingCompany ? (
                  <span style={styles.companyMiniSpinner} />
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24">
                    <path
                      d="M6 9l6 6 6-6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </span>
            </button>

            <AnimatePresence>
              {companyMenuOpen && (
                <motion.div
                  style={styles.companyMenu}
                  role="menu"
                  initial={{ opacity: 0, scale: 0.96, y: -6 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.98, y: -6 }}
                  transition={{ type: "spring", stiffness: 260, damping: 22 }}
                >
                  <div style={styles.companyMenuHeader}>
                    <span style={styles.companyMenuTitle}>
                      Empresas disponíveis
                    </span>
                    <span style={styles.companyMenuSubtitle}>
                      Selecione a empresa de trabalho
                    </span>
                  </div>

                  <div style={styles.companyList}>
                    {companyOptions.length === 0 ? (
                      <div style={styles.companyEmpty}>
                        Nenhuma empresa foi retornada para este usuário.
                      </div>
                    ) : (
                      companyOptions.map((empresa) => {
                        const selected = empresa.value === activeCompanyId;

                        return (
                          <button
                            key={empresa.value}
                            type="button"
                            style={{
                              ...styles.companyOption,
                              ...(selected ? styles.companyOptionSelected : {}),
                            }}
                            onClick={() => handleCompanyChange(empresa.value)}
                            disabled={isSwitchingCompany}
                          >
                            <span style={styles.companyOptionMain}>
                              <span style={styles.companyOptionName}>
                                {empresa.razaoSocial}
                              </span>

                              {selected && (
                                <span style={styles.companyOptionBadge}>
                                  Atual
                                </span>
                              )}
                            </span>

                            <span style={styles.companyOptionMeta}>
                              {empresa.subtitle}
                            </span>

                            {empresa.papel && (
                              <span style={styles.companyOptionRole}>
                                Perfil: {empresa.papel}
                              </span>
                            )}
                          </button>
                        );
                      })
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>

      <div style={styles.userInfo} ref={userMenuRef}>
        <motion.button
          type="button"
          style={{
            ...styles.userTrigger,
            ...(userAreaHovered ? styles.userTriggerHover : {}),
            ...(userMenuOpen ? styles.userTriggerOpen : {}),
          }}
          onMouseEnter={() => setUserAreaHovered(true)}
          onMouseLeave={() => setUserAreaHovered(false)}
          onClick={() => {
            setUserMenuOpen((prev) => !prev);
            setCompanyMenuOpen(false);
          }}
          aria-haspopup="menu"
          aria-expanded={userMenuOpen}
          whileHover={{ y: -0.5 }}
          whileTap={{ scale: 0.985 }}
          transition={{ type: "spring", stiffness: 320, damping: 24 }}
        >
          <span style={styles.avatarRing}>
            {profileUrl ? (
              <img
                src={profileUrl}
                alt={displayName}
                style={styles.avatar}
                draggable={false}
                loading="lazy"
              />
            ) : (
              <span style={{ ...styles.avatar, ...styles.avatarFallback }}>
                {hasName ? getInitials(nome) : ""}
              </span>
            )}
          </span>

          <span style={styles.userTextBlock}>
            <span
              style={{
                ...styles.name,
                color: userAreaHovered || userMenuOpen ? "#0F172A" : "#FFFFFF",
              }}
            >
              {displayName}
            </span>
          </span>

          <span
            style={{
              ...styles.chevron,
              transform: userMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
              color: userAreaHovered || userMenuOpen ? "#0F172A" : "#FFFFFF",
            }}
            aria-hidden="true"
          >
            <svg width="14" height="14" viewBox="0 0 24 24">
              <path
                d="M6 9l6 6 6-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </motion.button>

        <AnimatePresence>
          {userMenuOpen && (
            <motion.div
              style={styles.menu}
              role="menu"
              initial={{ opacity: 0, scale: 0.96, y: -6 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: -6 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
            >
              <div style={styles.accountHeader}>
                <span style={styles.accountAvatarLargeWrap}>
                  {profileUrl ? (
                    <img
                      src={profileUrl}
                      alt={displayName}
                      style={styles.accountAvatarLarge}
                      draggable={false}
                      loading="lazy"
                    />
                  ) : (
                    <span
                      style={{
                        ...styles.accountAvatarLarge,
                        ...styles.accountAvatarFallbackLarge,
                      }}
                    >
                      {hasName ? getInitials(nome) : ""}
                    </span>
                  )}
                </span>

                <div style={styles.accountIdentity}>
                  <span style={styles.accountName}>{displayName}</span>
                  <span style={styles.accountRole}>{departamento}</span>
                </div>
              </div>

              <div style={styles.accountDetails}>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Empresa</span>
                  <span style={styles.detailValue}>
                    {selectedCompany?.razaoSocial || empresaRazaoSocial}
                  </span>
                </div>

                {selectedCompanyCnpj && (
                  <div style={styles.detailRow}>
                    <span style={styles.detailLabel}>CNPJ</span>
                    <span style={styles.detailValue}>
                      {selectedCompanyCnpj}
                    </span>
                  </div>
                )}

                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Depto.</span>
                  <span style={styles.detailValue}>{departamento}</span>
                </div>
              </div>

              <div style={styles.logoutArea}>
                <button
                  type="button"
                  style={{
                    ...styles.logoutButton,
                    ...(loggingOut ? styles.logoutButtonDisabled : {}),
                  }}
                  onClick={doLogout}
                  disabled={loggingOut}
                >
                  <span style={styles.logoutIcon} aria-hidden="true">
                    {loggingOut ? (
                      <span style={styles.spinner} />
                    ) : (
                      <svg width="17" height="17" viewBox="0 0 24 24">
                        <path
                          d="M10 6H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h4"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M14 16l4-4-4-4"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8 12h10"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    )}
                  </span>

                  <span>
                    {loggingOut ? "Encerrando sessão..." : "Encerrar sessão"}
                  </span>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: "100%",
    height: "72px",
    minHeight: "72px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 24px",
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 999,
    boxSizing: "border-box",
    background: "transparent",
    border: "none",
    boxShadow: "none",
    overflow: "visible",
    pointerEvents: "none",
  },

  leftBlock: {
    display: "flex",
    alignItems: "center",
    marginRight: 18,
    flex: 1,
    minWidth: 0,
    pointerEvents: "auto",
  },

  systemInfo: {
    margin: 0,
    padding: 0,
    border: "none",
    background: "transparent",
    color: "#FFFFFF",
    fontFamily: '"Nunito", "Inter", sans-serif',
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
    maxWidth: "100%",
    textShadow: "0 2px 10px rgba(0,0,0,0.18)",
    whiteSpace: "nowrap",
    overflow: "visible",
    userSelect: "none",
  },

  logoButton: {
    margin: 0,
    padding: 0,
    border: "none",
    background: "transparent",
    color: "#FFFFFF",
    fontFamily: '"Nunito", "Inter", sans-serif',
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0,
    flexShrink: 0,
    cursor: "pointer",
    userSelect: "none",
  },

  systemLogo: {
    width: 44,
    height: 44,
    objectFit: "contain",
    flexShrink: 0,
    display: "block",
    marginTop: 5,
    pointerEvents: "none",
  },

  systemTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#FFFFFF",
    whiteSpace: "nowrap",
    textTransform: "uppercase",
    flexShrink: 0,
  },

  separator: {
    opacity: 0.65,
    color: "#FFFFFF",
    flexShrink: 0,
  },

  companySwitcher: {
    position: "relative",
    minWidth: 0,
    maxWidth: "min(860px, calc(100vw - 500px))",
    flex: 1,
    display: "flex",
    alignItems: "center",
  },

  companyButton: {
    width: "100%",
    minWidth: 0,
    border: "1px solid transparent",
    outline: "0",
    boxShadow: "none",
    background: "transparent",
    color: "#FFFFFF",
    fontFamily: '"Nunito", "Inter", sans-serif',
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 6,
    padding: "5px 8px",
    borderRadius: 999,
    cursor: "pointer",
    userSelect: "none",
    WebkitUserSelect: "none",
    WebkitTapHighlightColor: "transparent",
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    textShadow: "0 2px 10px rgba(0,0,0,0.18)",
    transition:
      "background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease",
  },

  companyButtonClosed: {
    background: "transparent",
    borderColor: "transparent",
    boxShadow: "none",
    outline: "0",
  },

  companyButtonOpen: {
    background: "rgba(255,255,255,0.08)",
    borderColor: "rgba(255,255,255,0.55)",
    boxShadow: "0 0 0 1px rgba(255,255,255,0.10), 0 10px 24px rgba(0,0,0,0.10)",
  },

  companyButtonDisabled: {
    opacity: 0.7,
    cursor: "wait",
  },

  companyInfo: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    color: "#FFFFFF",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
    flex: 1,
    textAlign: "left",
  },

  companyChevron: {
    width: 16,
    height: 16,
    minWidth: 16,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#FFFFFF",
    transition: "transform 0.18s ease",
    filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.24))",
  },

  companyMiniSpinner: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    border: "2px solid rgba(255,255,255,0.35)",
    borderTopColor: "#FFFFFF",
    animation: "spin 0.8s linear infinite",
  },

  companyMenu: {
    position: "absolute",
    top: "calc(100% + 12px)",
    left: 0,
    width: 460,
    maxWidth: "calc(100vw - 48px)",
    borderRadius: 10,
    background: "rgba(255,255,255,0.986)",
    border: "1px solid rgba(15,23,42,0.08)",
    boxShadow:
      "0 24px 70px rgba(0,0,0,0.24), 0 1px 0 rgba(255,255,255,0.86) inset",
    overflow: "hidden",
    zIndex: 1300,
    display: "flex",
    flexDirection: "column",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
  },

  companyMenuHeader: {
    padding: "14px 14px 12px",
    display: "flex",
    flexDirection: "column",
    gap: 3,
    background:
      "linear-gradient(180deg, rgba(248,250,252,0.96) 0%, rgba(255,255,255,0.98) 100%)",
    borderBottom: "1px solid rgba(15,23,42,0.06)",
  },

  companyMenuTitle: {
    color: "#0F172A",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 13.5,
    fontWeight: 900,
    lineHeight: 1.2,
  },

  companyMenuSubtitle: {
    color: "#64748B",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 11.5,
    fontWeight: 700,
    lineHeight: 1.25,
  },

  companyList: {
    padding: 8,
    display: "flex",
    flexDirection: "column",
    gap: 4,
    maxHeight: 300,
    overflowY: "auto",
    background: "#FFFFFF",
  },

  companyEmpty: {
    padding: "12px 10px",
    color: "#64748B",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.35,
  },

  companyOption: {
    width: "100%",
    border: "none",
    borderRadius: 10,
    background: "transparent",
    padding: "10px 10px",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: 4,
    textAlign: "left",
    transition: "background 0.16s ease, transform 0.16s ease",
  },

  companyOptionSelected: {
    background: "rgba(14,50,58,0.075)",
  },

  companyOptionMain: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    minWidth: 0,
  },

  companyOptionName: {
    minWidth: 0,
    color: "#0F172A",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 12.5,
    fontWeight: 900,
    lineHeight: 1.22,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  companyOptionBadge: {
    flexShrink: 0,
    height: 20,
    padding: "0 8px",
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(14,50,58,0.10)",
    color: "#0E323A",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 10,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  },

  companyOptionMeta: {
    color: "#64748B",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 11.2,
    fontWeight: 700,
    lineHeight: 1.25,
  },

  companyOptionRole: {
    color: "#94A3B8",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 10.8,
    fontWeight: 800,
    lineHeight: 1.25,
    textTransform: "uppercase",
  },

  userInfo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    marginLeft: "auto",
    position: "relative",
    minWidth: 0,
    flexShrink: 0,
    pointerEvents: "auto",
    zIndex: 5100,
  },

  userTrigger: {
    height: 44,
    maxWidth: 260,
    display: "inline-flex",
    alignItems: "center",
    gap: 9,
    padding: "4px 10px 4px 5px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.055)",
    cursor: "pointer",
    userSelect: "none",
    outline: "none",
    color: "#FFFFFF",
    boxSizing: "border-box",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    boxShadow: "0 1px 0 rgba(255,255,255,0.08) inset",
    transition:
      "background 0.22s ease, box-shadow 0.22s ease, transform 0.18s ease, border 0.22s ease, color 0.22s ease",
  },

  userTriggerHover: {
    background: "rgba(255,255,255,0.96)",
    border: "1px solid rgba(255,255,255,0.60)",
    boxShadow:
      "0 12px 28px rgba(0,0,0,0.12), 0 1px 0 rgba(255,255,255,0.80) inset",
  },

  userTriggerOpen: {
    background: "rgba(255,255,255,0.98)",
    border: "1px solid rgba(255,255,255,0.68)",
    boxShadow:
      "0 14px 34px rgba(0,0,0,0.16), 0 1px 0 rgba(255,255,255,0.86) inset",
  },

  avatarRing: {
    width: 34,
    height: 34,
    minWidth: 34,
    minHeight: 34,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    boxSizing: "border-box",
    background: "transparent",
    boxShadow: "none",
  },

  avatar: {
    width: 32,
    height: 32,
    minWidth: 32,
    minHeight: 32,
    borderRadius: "50%",
    objectFit: "cover",
    userSelect: "none",
    background: "#0B5268",
    display: "block",
  },

  avatarFallback: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#FFFFFF",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.04em",
    background: "linear-gradient(180deg, #0B5268 0%, #073D4E 100%)",
  },

  userTextBlock: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  name: {
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1,
    whiteSpace: "nowrap",
    maxWidth: 160,
    overflow: "hidden",
    textOverflow: "ellipsis",
    transition: "color 0.2s ease",
  },

  chevron: {
    width: 16,
    height: 16,
    minWidth: 16,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "transform 0.18s ease, color 0.18s ease",
  },

  menu: {
    position: "absolute",
    top: "calc(100% + 12px)",
    right: 0,
    width: 326,
    borderRadius: 8,
    background: "rgba(255,255,255,0.982)",
    border: "1px solid rgba(15,23,42,0.08)",
    boxShadow:
      "0 24px 70px rgba(0,0,0,0.24), 0 1px 0 rgba(255,255,255,0.86) inset",
    overflow: "hidden",
    zIndex: 1200,
    display: "flex",
    flexDirection: "column",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
  },

  accountHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "16px 16px 13px",
    background:
      "linear-gradient(180deg, rgba(248,250,252,0.96) 0%, rgba(255,255,255,0.98) 100%)",
  },

  accountAvatarLargeWrap: {
    width: 48,
    height: 48,
    minWidth: 48,
    minHeight: 48,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    boxSizing: "border-box",
    background: "transparent",
    boxShadow: "none",
  },

  accountAvatarLarge: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    objectFit: "cover",
    background: "#0B5268",
    display: "block",
  },

  accountAvatarFallbackLarge: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#FFFFFF",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 15,
    fontWeight: 900,
    letterSpacing: "0.04em",
    background: "linear-gradient(180deg, #0B5268 0%, #073D4E 100%)",
  },

  accountIdentity: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },

  accountName: {
    color: "#0F172A",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 15,
    fontWeight: 900,
    lineHeight: 1.15,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  accountRole: {
    color: "#64748B",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.2,
  },

  accountDetails: {
    padding: "10px 16px 12px",
    borderTop: "1px solid rgba(15,23,42,0.06)",
    borderBottom: "1px solid rgba(15,23,42,0.06)",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    background: "#FFFFFF",
  },

  detailRow: {
    display: "grid",
    gridTemplateColumns: "70px 1fr",
    gap: 10,
    alignItems: "start",
  },

  detailLabel: {
    color: "#94A3B8",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 10.5,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    lineHeight: 1.35,
  },

  detailValue: {
    color: "#334155",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 11.5,
    fontWeight: 800,
    lineHeight: 1.35,
    textTransform: "uppercase",
    overflowWrap: "anywhere",
  },

  logoutArea: {
    padding: "10px",
    background: "rgba(248,250,252,0.96)",
    borderTop: "1px solid rgba(15,23,42,0.06)",
  },

  logoutButton: {
    width: "100%",
    minHeight: 43,
    borderRadius: 12,
    border: "1px solid rgba(185,28,28,0.13)",
    background: "rgba(254,242,242,0.82)",
    color: "#991B1B",
    fontFamily: '"Nunito", "Inter", sans-serif',
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transition:
      "background 0.16s ease, border-color 0.16s ease, transform 0.16s ease, opacity 0.16s ease",
  },

  logoutButtonDisabled: {
    opacity: 0.72,
    cursor: "not-allowed",
  },

  logoutIcon: {
    width: 18,
    height: 18,
    minWidth: 18,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  spinner: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    border: "2px solid rgba(153,27,27,0.25)",
    borderTopColor: "#991B1B",
    animation: "spin 0.8s linear infinite",
  },
};

export default HeaderHome;
